import { httpClient } from './http-client';
import {
  Account,
  AccountWithBalance,
  AccountWithSnapshot,
  AccountSummary,
  CreateAccountRequest,
  UpdateAccountRequest,
  AccountFilter,
  ApiResponse,
  PaginatedResponse,
  NetWorthSummary,
} from '../types/api';

export class AccountsService {
  private static readonly ENDPOINTS = {
    ACCOUNTS: '/accounts',
    ACCOUNT_BY_ID: (id: string) => `/accounts/${id}`,
    ACCOUNT_SUMMARY: (id: string) => `/accounts/${id}/summary`,
    ACCOUNTS_WITH_BALANCES: '/accounts/with-balances',
    NET_WORTH: '/accounts/net-worth',
  } as const;

  /**
   * Create a new account
   */
  async createAccount(accountData: CreateAccountRequest): Promise<ApiResponse<Account>> {
    return httpClient.post(AccountsService.ENDPOINTS.ACCOUNTS, accountData);
  }

  /**
   * Set the value of a manually-tracked account -- a house, a car, anything
   * no institution reports.
   *
   * Posts the DIFFERENCE against equity rather than replacing the balance, so
   * the ledger stays balanced and the history of what the asset was said to be
   * worth, and when, survives. `value` is signed: a debt is negative.
   */
  async setValuation(
    accountId: string,
    body: { value: string; valued_at?: string; note?: string; vin?: string },
  ): Promise<ApiResponse<{ ok: boolean }>> {
    return httpClient.put(
      `${AccountsService.ENDPOINTS.ACCOUNTS}/${accountId}/valuation`, body);
  }

  /**
   * Get all accounts with optional filtering and pagination
   */
  async getAccounts(filter?: AccountFilter): Promise<ApiResponse<PaginatedResponse<Account>>> {
    const searchParams = new URLSearchParams();
    
    if (filter) {
      if (filter.account_type) searchParams.set('account_type', filter.account_type);
      if (filter.currency) searchParams.set('currency', filter.currency);
      if (filter.is_active !== undefined) searchParams.set('is_active', filter.is_active.toString());
      if (filter.page) searchParams.set('page', filter.page.toString());
      if (filter.per_page) searchParams.set('per_page', filter.per_page.toString());
    }

    const url = searchParams.toString() 
      ? `${AccountsService.ENDPOINTS.ACCOUNTS}?${searchParams.toString()}`
      : AccountsService.ENDPOINTS.ACCOUNTS;

    const rawResponse = await httpClient.get<any>(url);
    
    // Backend returns: { accounts: [], total: number, page: number, per_page: number }
    // Handle null/undefined response
    if (!rawResponse) {
      return {
        data: {
          data: [],
          total: 0,
          page: 1,
          perPage: 20,
          totalPages: 0,
        },
      };
    }
    
    // Wrap it to match the expected ApiResponse<PaginatedResponse<T>> format
    return {
      data: {
        data: rawResponse.accounts || [],
        total: rawResponse.total || 0,
        page: rawResponse.page || 1,
        perPage: rawResponse.per_page || 20,
        totalPages: rawResponse.per_page ? Math.ceil((rawResponse.total || 0) / rawResponse.per_page) : 0,
      },
    };
  }

  /**
   * Every account matching the filter, across as many pages as it takes.
   *
   * Pickers must use this, not one getAccounts page. The API caps a page at
   * 100 and used to answer an oversize request with its default of 20 -- so a
   * picker that asked for 200 quietly offered the 20 newest accounts, and the
   * member's own older chequing account was simply not there to pick.
   *
   * @param filter Optional type/currency/active filter; paging is handled here.
   * @returns All matching accounts.
   */
  async getAllAccounts(filter?: Omit<AccountFilter, 'page' | 'per_page'>): Promise<Account[]> {
    const perPage = 100;
    const all: Account[] = [];
    // Bounded so a server that ignores `page` cannot loop forever.
    for (let page = 1; page <= 50; page++) {
      const res = await this.getAccounts({ ...filter, page, per_page: perPage });
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      all.push(...rows);
      const total = res?.data?.total ?? 0;
      if (rows.length === 0 || all.length >= total) break;
    }
    return all;
  }

  /**
   * Get a specific account by ID
   */
  async getAccount(id: string): Promise<ApiResponse<Account>> {
    return httpClient.get(AccountsService.ENDPOINTS.ACCOUNT_BY_ID(id));
  }

  /**
   * Update an existing account
   */
  async updateAccount(id: string, updates: UpdateAccountRequest): Promise<ApiResponse<Account>> {
    return httpClient.put(AccountsService.ENDPOINTS.ACCOUNT_BY_ID(id), updates);
  }

  /**
   * Delete an account
   */
  async deleteAccount(id: string): Promise<ApiResponse<void>> {
    return httpClient.delete(AccountsService.ENDPOINTS.ACCOUNT_BY_ID(id));
  }

  /**
   * Deactivate an account (soft delete)
   */
  async deactivateAccount(id: string): Promise<ApiResponse<Account>> {
    return httpClient.patch(AccountsService.ENDPOINTS.ACCOUNT_BY_ID(id), { is_active: false });
  }

  /**
   * Reactivate an account
   */
  async reactivateAccount(id: string): Promise<ApiResponse<Account>> {
    return httpClient.patch(AccountsService.ENDPOINTS.ACCOUNT_BY_ID(id), { is_active: true });
  }

  /**
   * Get account summary with latest balance
   */
  async getAccountSummary(id: string): Promise<ApiResponse<AccountSummary>> {
    return httpClient.get(AccountsService.ENDPOINTS.ACCOUNT_SUMMARY(id));
  }

  /**
   * Get all accounts with their latest balances
   * Normalizes both raw-array and wrapped ApiResponse shapes
   */
  async getAccountsWithBalances(ownerUserIds?: string[]): Promise<ApiResponse<AccountWithBalance[]>> {
    let url: string = AccountsService.ENDPOINTS.ACCOUNTS_WITH_BALANCES;
    if (ownerUserIds && ownerUserIds.length > 0) {
      const params = new URLSearchParams();
      params.set('owner_user_ids', ownerUserIds.join(','));
      url = `${url}?${params.toString()}`;
    }

    const raw = await httpClient.get<AccountWithBalance[] | ApiResponse<AccountWithBalance[]>>(url as any);
    const data: AccountWithBalance[] = (raw as any)?.data ? (raw as any).data : (raw as AccountWithBalance[]);
    return { data };
  }

  

  /**
   * Get household net worth summary
   */
  async getNetWorth(): Promise<ApiResponse<NetWorthSummary>> {
    return httpClient.get(AccountsService.ENDPOINTS.NET_WORTH);
  }

  /**
   * Get accounts filtered by type
   */
  async getAccountsByType(accountType: string): Promise<ApiResponse<Account[]>> {
    return this.getAccounts({ account_type: accountType as any }).then(response => ({
      data: response.data.data,
      message: response.message,
      error: response.error,
    }));
  }

  /**
   * Get active accounts only
   */
  async getActiveAccounts(): Promise<ApiResponse<Account[]>> {
    return this.getAccounts({ is_active: true }).then(response => ({
      data: response.data.data,
      message: response.message,
      error: response.error,
    }));
  }

  /**
   * Search accounts by name
   */
  async searchAccounts(query: string): Promise<ApiResponse<Account[]>> {
    // This would require backend support for search
    // For now, we'll get all accounts and filter client-side
    const response = await this.getAccounts();
    const filteredAccounts = response.data.data.filter(account =>
      account.name.toLowerCase().includes(query.toLowerCase()) ||
      account.institution.toLowerCase().includes(query.toLowerCase())
    );

    return {
      data: filteredAccounts,
      message: response.message,
      error: response.error,
    };
  }

  /**
   * Get account statistics
   */
  async getAccountStats(): Promise<{
    totalAccounts: number;
    activeAccounts: number;
    accountsByType: Record<string, number>;
    accountsByCurrency: Record<string, number>;
  }> {
    const response = await this.getAccounts();
    const accounts = response.data.data;

    const stats = {
      totalAccounts: accounts.length,
      activeAccounts: accounts.filter(a => a.is_active).length,
      accountsByType: {} as Record<string, number>,
      accountsByCurrency: {} as Record<string, number>,
    };

    accounts.forEach(account => {
      // Count by type
      stats.accountsByType[account.account_type] = 
        (stats.accountsByType[account.account_type] || 0) + 1;

      // Count by currency
      stats.accountsByCurrency[account.currency] = 
        (stats.accountsByCurrency[account.currency] || 0) + 1;
    });

    return stats;
  }
}

// Export singleton instance
export const accountsService = new AccountsService();
